import PrimaryButton from '@/components/Buttons/PrimaryButton';
import ThemeWrapper from '@/components/ThemeWrapper';
import usePermissions from '@/hooks/usePermissions';
import useRoutes from '@/hooks/useRoutes';
import StaffLayout from '@/layouts/StaffLayout';
import { User } from '@/types';
import { Link } from '@inertiajs/react';
import { Edit, UserPlus } from 'lucide-react';
import React from 'react';

interface CommercialUsersIndexProps {
    users: User[];
}

const IndexContent: React.FC<CommercialUsersIndexProps> = ({ users }) => {
    const { canManageCommercialUsers } = usePermissions();
    const { route } = useRoutes();

    return (
        <StaffLayout>
            <div className="mx-auto max-w-7xl">
                <div className="mb-8 flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                            Usuarios Comerciales
                        </h1>
                        <p className="mt-2 text-gray-600 dark:text-neutral-300">
                            Gestión de usuarios del equipo comercial
                        </p>
                    </div>
                    {canManageCommercialUsers && (
                        <Link
                            href={route(
                                'staff-commercial-users-create'
                            ).toString()}
                        >
                            <PrimaryButton>
                                <UserPlus size={16} className="mr-2" />
                                Crear Usuario
                            </PrimaryButton>
                        </Link>
                    )}
                </div>

                {users.length === 0 ? (
                    <div className="rounded-lg bg-white dark:bg-neutral-800 p-12 text-center shadow">
                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-neutral-700">
                            <UserPlus size={32} className="text-gray-400 dark:text-neutral-500" />
                        </div>
                        <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                            No hay usuarios comerciales
                        </h3>
                        <p className="mb-6 text-gray-600 dark:text-neutral-300">
                            Comienza creando tu primer usuario comercial.
                        </p>
                        {canManageCommercialUsers && (
                            <Link
                                href={route(
                                    'staff-commercial-users-create'
                                ).toString()}
                            >
                                <PrimaryButton>
                                    <UserPlus size={16} className="mr-2" />
                                    Crear Usuario
                                </PrimaryButton>
                            </Link>
                        )}
                    </div>
                ) : (
                    <div className="overflow-hidden rounded-lg bg-white dark:bg-neutral-800 shadow">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-700">
                            <thead className="bg-gray-50 dark:bg-neutral-700">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 dark:text-neutral-400 uppercase">
                                        Nombre
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 dark:text-neutral-400 uppercase">
                                        Email
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 dark:text-neutral-400 uppercase">
                                        Academia
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 dark:text-neutral-400 uppercase">
                                        Estado
                                    </th>
                                    {canManageCommercialUsers && (
                                        <th className="px-6 py-3 text-right text-xs font-medium tracking-wider text-gray-500 dark:text-neutral-400 uppercase">
                                            Acciones
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-neutral-700 bg-white dark:bg-neutral-800">
                                {users.map((user) => (
                                    <tr key={user.id}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                {user.full_name}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-500 dark:text-neutral-400">
                                                {user.email}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 dark:text-white">
                                                {user.current_academy?.name ||
                                                    '-'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {user.is_active ? (
                                                <span className="inline-flex rounded-full bg-green-100 px-2 text-xs leading-5 font-semibold text-green-800">
                                                    Activo
                                                </span>
                                            ) : (
                                                <span className="inline-flex rounded-full bg-red-100 px-2 text-xs leading-5 font-semibold text-red-800">
                                                    Inactivo
                                                </span>
                                            )}
                                        </td>
                                        {canManageCommercialUsers && (
                                            <td className="px-6 py-4 text-right text-sm font-medium whitespace-nowrap">
                                                <Link
                                                    href={route(
                                                        'staff-commercial-users-edit',
                                                        { user_id: user.id }
                                                    ).toString()}
                                                    className="mr-3 inline-flex items-center text-blue-600 hover:text-blue-900"
                                                >
                                                    <Edit size={16} />
                                                </Link>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </StaffLayout>
    );
};

const Index: React.FC<CommercialUsersIndexProps> = (props) => {
    return (
        <ThemeWrapper>
            <IndexContent {...props} />
        </ThemeWrapper>
    );
};

export default Index;
